import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  AnalyticsValidationResult,
  formatValidationErrors,
  validateAnalyticsEvent
} from './analytics-events.schema';

/**
 * The one and only way this app writes to the GTM `window.dataLayer`.
 *
 * `scripts/check-analytics-usage.js` fails the build if any component reaches
 * for `window.dataLayer.push` directly, so every tracked event necessarily
 * passes through the schema declared in `analytics-events.schema.ts`.
 *
 * Validation failures never break the site: outside development the invalid
 * payload is still pushed and the problem is reported to the console. The build
 * gate is where a bad payload is meant to be caught.
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  /**
   * When true, an invalid payload throws instead of warning. Specs turn this on
   * so a contract violation fails loudly rather than scrolling past in a log.
   */
  static strict = false;

  /**
   * Push a declared event. `payload` must not carry its own `event` key — the
   * event name is always the first argument so it stays greppable.
   */
  push(eventName: string, payload: { [key: string]: any } = {}): AnalyticsValidationResult {
    const event = { event: eventName, ...payload };
    const result = validateAnalyticsEvent(event);

    if (!result.valid) {
      this.reportInvalid(eventName, result.errors);
    }

    this.dataLayer().push(event);
    this.debug(eventName, event);

    return result;
  }

  /**
   * Push several events of the same name in order — used for the per-leg
   * `payment_initiated` and `purchase` events on return trips. Null entries are
   * skipped so callers can build a leg conditionally.
   */
  pushEach(eventName: string, payloads: Array<{ [key: string]: any } | null | undefined>): void {
    (payloads || []).forEach(payload => {
      if (payload) {
        this.push(eventName, payload);
      }
    });
  }

  /**
   * Clear the previous enhanced-ecommerce object. GTM requires this between
   * ecommerce pushes or fields leak from one event into the next.
   */
  resetEcommerce(): void {
    this.dataLayer().push({ ecommerce: null });
  }

  /**
   * The live dataLayer array, created if GTM has not initialised it yet.
   */
  dataLayer(): any[] {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  private reportInvalid(eventName: string, errors: string[]): void {
    const message = formatValidationErrors(eventName, errors);

    if (AnalyticsService.strict) {
      throw new Error(message);
    }

    console.error(`[analytics] ${message}`);
  }

  private debug(eventName: string, payload: any): void {
    if (environment.production) {
      return;
    }

    console.log(`[dataLayer][debug] ${eventName}`, payload);
  }
}
